import { observer } from 'mobx-react';
import React, { PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { Button, Tooltip } from '@mui/material';

import useLicenseInfo from 'hooks/use-license-info';
import useStores from 'hooks/use-stores';

import ChevronUpDoubleIcon from './assets/chevron-up-double.svg';
import useStyles from './styles';

const ActionTooltip: React.FC<PropsWithChildren> = ({ children }) => {
    const history = useHistory();
    const { classes } = useStyles();
    const { userStore } = useStores();
    const { isTrial } = useLicenseInfo();
    const [isOpen, setIsOpen] = useState(false);

    const goToUserInfo = useCallback(() => history.push('/userinfo'), [history]);

    const isCloseToLimit = userStore.userCredits / (userStore.userCredits + userStore.licenseSpent) <= 0.1;
    const hasReachedLimit = userStore.userCredits === 0;

    const info = useMemo(() => {
        switch (true) {
            case isTrial && hasReachedLimit:
                return {
                    description: (
                        <>
                            30-day trial has ended. Upgrade your plan to continue enjoying{' '}
                            <strong>our services!</strong>
                        </>
                    ),
                    button: {
                        text: 'Upgrade plan',
                        icon: <ChevronUpDoubleIcon />,
                        onClick: goToUserInfo
                    }
                };
            case isTrial && isCloseToLimit && !hasReachedLimit:
                return {
                    description: (
                        <>
                            Your trial plan is about to expire. Upgrade your plan to continue enjoying{' '}
                            <strong>our services!</strong>
                        </>
                    ),
                    button: {
                        text: 'Upgrade plan',
                        icon: <ChevronUpDoubleIcon />,
                        onClick: goToUserInfo
                    }
                };
            case !isTrial && hasReachedLimit:
                return {
                    description: (
                        <>
                            You have reached your request limit. Upgrade your plan to get{' '}
                            <strong>unlimited requests!</strong>
                        </>
                    ),
                    button: {
                        text: 'Upgrade plan',
                        icon: <ChevronUpDoubleIcon />,
                        onClick: goToUserInfo
                    }
                };
            case !isTrial && isCloseToLimit && !hasReachedLimit:
                return {
                    description: (
                        <>
                            You are nearing your request limit. Upgrade your plan to get{' '}
                            <strong>unlimited requests!</strong>
                        </>
                    ),
                    button: {
                        text: 'Upgrade plan',
                        icon: <ChevronUpDoubleIcon />,
                        onClick: goToUserInfo
                    }
                };
            default:
                return null;
        }
    }, [goToUserInfo, hasReachedLimit, isCloseToLimit, isTrial]);

    return React.isValidElement(children) ? (
        <Tooltip
            placement="top"
            arrow
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            open={isOpen}
            classes={{ tooltip: classes.tooltip, arrow: classes.arrow }}
            title={
                info ? (
                    <div className={classes.content}>
                        <p className={classes.description}>{info.description}</p>
                        <Button
                            fullWidth
                            disableElevation
                            disableRipple
                            disableFocusRipple
                            variant="contained"
                            startIcon={info.button.icon}
                            onClick={() => {
                                info.button.onClick();
                                setIsOpen(false);
                            }}
                            className={classes.button}
                        >
                            {info.button.text}
                        </Button>
                    </div>
                ) : null
            }
        >
            {children}
        </Tooltip>
    ) : null;
};

export default observer(ActionTooltip);
